import React, {
  useCallback, useMemo, useState, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import ExpansionGroup from '../../../components/expansion-group/expansion-group.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import useScreenSmall from '../../../hooks/use-screen-small';
import { DEAL_COMMON_EDIT } from '../../../constants/entity/permissions';

const DealCapitalizationCommonData: ReactMemoComponent = React.memo((): ReactElement => {
  const isSmallScreen: boolean = useScreenSmall();

  const [isExpanded, setIsExpanded]: [boolean, Function] = useState(false);

  const dealCommonEditPermissions: string[] = useMemo((): string[] => [DEAL_COMMON_EDIT], []);

  const formPrefix: string = 'dealData.dealCommonData';

  const onExpandPanel: Function = useCallback((): void => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  return (
    <CheckAccess permissions={dealCommonEditPermissions}>
      <ExpansionGroup
        withOffset
        withExpandedOffset
        isExpanded={isExpanded}
        headerComponent="Capitalization Common Info"
        onChange={onExpandPanel}
      >
        <Card isContainer>
          <Table>
            <TableBody
              withNoBorders
              withTopAlign
              withSmallPadding
              withTwoEqualColumns={!isSmallScreen}
              withBlockCells={isSmallScreen}
            >
              <TableRow>
                <TableCell>
                  <FormFieldNumber
                    isOutlined
                    name={`${formPrefix}.amount`}
                    label="Total Capitalization"
                  />
                </TableCell>
                <TableCell />
              </TableRow>
              <TableRow>
                <TableCell>
                  <FormFieldNumber
                    isFloat
                    isDisabled
                    isOutlined
                    name={`${formPrefix}.zfaAmount`}
                    label="ZFA"
                  />
                </TableCell>
                <TableCell>
                  <FormFieldNumber
                    isFloat
                    isDisabled
                    isOutlined
                    name={`${formPrefix}.gsfAmount`}
                    label="GSF"
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <FormFieldNumber
                    isFloat
                    isDisabled
                    isOutlined
                    name={`${formPrefix}.nsfAmount`}
                    label="NSF"
                  />
                </TableCell>
                <TableCell>
                  <FormFieldNumber
                    isFloat
                    isDisabled
                    isOutlined
                    name={`${formPrefix}.unitsAmount`}
                    label="Units"
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <FormFieldNumber
                    isFloat
                    isDisabled
                    isOutlined
                    name={`${formPrefix}.keysAmount`}
                    label="Keys"
                  />
                </TableCell>
                <TableCell>
                  <FormFieldNumber
                    isFloat
                    isDisabled
                    isOutlined
                    name={`${formPrefix}.acresAmount`}
                    label="Acres"
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <FormFieldNumber
                    isFloat
                    isDisabled
                    isOutlined
                    name={`${formPrefix}.lotsfAmount`}
                    label="Lot SF"
                  />
                </TableCell>
                <TableCell />
              </TableRow>
              <TableRow>
                <TableCell>
                  <FormFieldText isOutlined name={`${formPrefix}.dealTermValue`} label="Deal Term" />
                </TableCell>
                <TableCell>
                  <FormFieldNumber isOutlined name={`${formPrefix}.projectedNoiAmount`} label="Projected NOI" />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Card>
      </ExpansionGroup>
    </CheckAccess>
  );
});

export default DealCapitalizationCommonData;
